import dayjs from 'dayjs';
import { RefObject, useEffect, useState } from 'react';

/** Colonna di un giorno nell'intestazione di Syncfusion. */
export interface ColonnaGiorno {
  /** YYYY-MM-DD, il giorno mostrato dalla colonna. */
  giorno: string;
  larghezza: number;
}

export interface ColonneGiorni {
  /** Larghezza della colonna delle ore: è la colonna dell'etichetta della fascia. */
  sinistra: number;
  /** Spazio dopo l'ultimo giorno (il padding della scrollbar dell'intestazione). */
  destra: number;
  /** Ultime colonne misurate, nell'ordine; null = nessuna misura ancora. */
  colonne: ColonnaGiorno[] | null;
}

interface MisuraIntestazione {
  sinistra: number;
  destra: number;
  colonne: ColonnaGiorno[];
}

// Colonna delle ore della vista desktop, finché non c'è una misura.
const SINISTRA_PREDEFINITA = 85;

const arrotonda = (valore: number): number => Math.round(valore * 100) / 100;

const isUguale = (
  a: MisuraIntestazione | null,
  b: MisuraIntestazione
): boolean =>
  !!a &&
  a.sinistra === b.sinistra &&
  a.destra === b.destra &&
  a.colonne.length === b.colonne.length &&
  a.colonne.every(
    (colonna, i) =>
      colonna.giorno === b.colonne[i].giorno &&
      colonna.larghezza === b.colonne[i].larghezza
  );

// Posizioni delle celle dei giorni rispetto alla fascia, che sta sopra il calendario
// e ha la sua stessa larghezza.
const misuraIntestazione = (
  fascia: HTMLElement,
  intestazione: Element
): MisuraIntestazione | null => {
  const celle = Array.from(
    intestazione.querySelectorAll<HTMLElement>('td.e-header-cells')
  );
  const rettangoloFascia = fascia.getBoundingClientRect();
  // Calendario non ancora disegnato o nascosto: si tengono le misure di prima.
  if (celle.length === 0 || rettangoloFascia.width === 0) return null;
  const rettangoli = celle.map((cella) => cella.getBoundingClientRect());
  const ultimo = rettangoli[rettangoli.length - 1];
  return {
    sinistra: arrotonda(rettangoli[0].left - rettangoloFascia.left),
    destra: arrotonda(Math.max(0, rettangoloFascia.right - ultimo.right)),
    colonne: celle.map((cella, i) => ({
      // data-date è il timestamp della mezzanotte locale: con toISOString il giorno
      // potrebbe diventare quello prima.
      giorno: dayjs(Number(cella.getAttribute('data-date'))).format(
        'YYYY-MM-DD'
      ),
      larghezza: arrotonda(rettangoli[i].width),
    })),
  };
};

/**
 * Colonne dei giorni dell'agenda negozio, misurate sulle celle d'intestazione di
 * Syncfusion (`#calendar-container .e-date-header-wrap td.e-header-cells`): la fascia
 * Presenze sta fuori dallo Scheduler e si allinea alle colonne vere, comprese le
 * larghezze che useWorkWeekColumns scrive sui col nella vista Lavorativo.
 *
 * Tre osservatori:
 * - MO-A sul contenitore del calendario: si accorge quando Syncfusion ricrea
 *   l'intestazione (lo Scheduler viene smontato a ogni cambio di data, vista o
 *   utenti);
 * - MO-B sullo style della tabella d'intestazione: le scritture sui col, anche in
 *   ritardo;
 * - ResizeObserver sull'intestazione: resize della finestra e pannello di destra.
 * Quando la tabella cambia si staccano tutti e tre e si riagganciano ai nodi nuovi,
 * mai osservatori su nodi staccati. Le misure vecchie restano finché non compaiono le
 * celle nuove.
 *
 * In Agenda non c'è un'intestazione da misurare: la fascia è una sola cella a tutta
 * larghezza, le chip vanno a capo e non serve nessuna misura.
 */
export const useColonneGiorni = (
  fasciaRef: RefObject<HTMLElement>,
  agenda: boolean
): ColonneGiorni => {
  const [misura, setMisura] = useState<MisuraIntestazione | null>(null);

  useEffect(() => {
    const fascia = fasciaRef.current;
    // Il contenitore accanto alla fascia: lo stesso id c'è nelle altre agende del
    // pacchetto.
    const contenitore = fascia?.parentElement?.querySelector<HTMLElement>(
      '#calendar-container'
    );
    if (
      agenda ||
      !fascia ||
      !contenitore ||
      typeof MutationObserver === 'undefined'
    )
      return;

    // undefined = mai agganciata; null = nessuna intestazione nel calendario.
    let tabella: Element | null | undefined;
    let frameAggancio = 0;
    let frameMisura = 0;

    const misuraNelFrame = () => {
      cancelAnimationFrame(frameMisura);
      frameMisura = requestAnimationFrame(() => {
        const intestazione = tabella?.closest('.e-date-header-wrap');
        if (!intestazione || !intestazione.isConnected) return;
        const nuova = misuraIntestazione(fascia, intestazione);
        if (nuova)
          setMisura((precedente) =>
            isUguale(precedente, nuova) ? precedente : nuova
          );
      });
    };

    // MO-B (style dei col) e ResizeObserver (larghezza dell'intestazione).
    const osservatoreTabella = new MutationObserver(misuraNelFrame);
    const osservatoreDimensioni =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(misuraNelFrame)
        : null;

    // MO-A: callback accorpata in un frame, che fa solo un querySelector.
    const osservatoreContenitore = new MutationObserver(() => {
      cancelAnimationFrame(frameAggancio);
      frameAggancio = requestAnimationFrame(aggancia);
    });

    // Se la tabella d'intestazione è cambiata stacca tutti e tre gli osservatori e
    // li riaggancia ai nodi nuovi; senza tabella resta solo MO-A, in attesa.
    const aggancia = () => {
      const nuova = contenitore.querySelector('.e-date-header-wrap table');
      if (nuova === tabella) return;
      osservatoreContenitore.disconnect();
      osservatoreTabella.disconnect();
      osservatoreDimensioni?.disconnect();
      tabella = nuova;
      osservatoreContenitore.observe(contenitore, {
        childList: true,
        subtree: true,
      });
      if (!tabella) return;
      osservatoreTabella.observe(tabella, {
        attributes: true,
        subtree: true,
        attributeFilter: ['style'],
      });
      const intestazione = tabella.closest('.e-date-header-wrap');
      if (intestazione) osservatoreDimensioni?.observe(intestazione);
      misuraNelFrame();
    };

    aggancia();

    return () => {
      osservatoreContenitore.disconnect();
      osservatoreTabella.disconnect();
      osservatoreDimensioni?.disconnect();
      cancelAnimationFrame(frameAggancio);
      cancelAnimationFrame(frameMisura);
    };
  }, [agenda, fasciaRef]);

  return {
    sinistra: misura?.sinistra ?? SINISTRA_PREDEFINITA,
    destra: misura?.destra ?? 0,
    colonne: misura?.colonne ?? null,
  };
};
